const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidDateOnly(value) {
  if (typeof value !== 'string' || !YMD.test(value)) return false;
  const [, ys, ms, ds] = value.match(YMD);
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function normalizeDateRange(from, to) {
  if (!isValidDateOnly(from) || !isValidDateOnly(to)) return null;
  return from <= to ? { from, to } : { from: to, to: from };
}

export function coerceAnalyticsWindow(windowOrDays) {
  if (windowOrDays && typeof windowOrDays === 'object') {
    if (windowOrDays.mode === 'custom' && isValidDateOnly(windowOrDays.from) && isValidDateOnly(windowOrDays.to)) {
      const range = normalizeDateRange(windowOrDays.from, windowOrDays.to);
      return { mode: 'custom', from: range.from, to: range.to };
    }
    const days = String(windowOrDays.days ?? 30);
    return { mode: 'preset', days };
  }
  return { mode: 'preset', days: String(windowOrDays ?? 30) };
}

export function appendAnalyticsWindow(params, windowOrDays) {
  const window = coerceAnalyticsWindow(windowOrDays);
  if (window.mode === 'custom') {
    params.set('from', window.from);
    params.set('to', window.to);
    params.delete('days');
    return params;
  }
  params.set('days', window.days);
  return params;
}

export function addDaysYmd(ymd, days) {
  const [, ys, ms, ds] = String(ymd).match(YMD) || [];
  const dt = new Date(Date.UTC(Number(ys), Number(ms) - 1, Number(ds)));
  dt.setUTCDate(dt.getUTCDate() + Number(days));
  return dt.toISOString().slice(0, 10);
}

export function inclusiveDayCount(from, to) {
  const range = normalizeDateRange(from, to);
  if (!range) return 0;
  const a = Date.parse(`${range.from}T00:00:00Z`);
  const b = Date.parse(`${range.to}T00:00:00Z`);
  return Math.round((b - a) / 86400000) + 1;
}

export function windowDayCount(windowOrDays) {
  const window = coerceAnalyticsWindow(windowOrDays);
  if (window.mode === 'custom') return inclusiveDayCount(window.from, window.to);
  return Math.max(1, parseInt(window.days, 10) || 30);
}

export function previousInclusiveRange(from, to) {
  const range = normalizeDateRange(from, to);
  if (!range) return null;
  const n = inclusiveDayCount(range.from, range.to);
  const prevTo = addDaysYmd(range.from, -1);
  const prevFrom = addDaysYmd(prevTo, -(n - 1));
  return { from: prevFrom, to: prevTo };
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parts(ymd) {
  const [, y, m, d] = ymd.match(YMD);
  return { y: Number(y), m: Number(m), d: Number(d), month: MONTHS[Number(m) - 1] };
}

export function formatInclusiveRangeLabel(from, to) {
  const range = normalizeDateRange(from, to);
  if (!range) return '';
  const a = parts(range.from);
  const b = parts(range.to);
  if (a.y === b.y && a.m === b.m && a.d === b.d) return `${a.d} ${a.month} ${a.y}`;
  if (a.y === b.y && a.m === b.m) return `${a.d}–${b.d} ${a.month} ${a.y}`;
  if (a.y === b.y) return `${a.d} ${a.month}–${b.d} ${b.month} ${a.y}`;
  return `${a.d} ${a.month} ${a.y}–${b.d} ${b.month} ${b.y}`;
}

export function formatAnalyticsWindowLabel(windowOrDays, { lastNPrefix = 'Last' } = {}) {
  const window = coerceAnalyticsWindow(windowOrDays);
  if (window.mode === 'custom') return formatInclusiveRangeLabel(window.from, window.to);
  const n = windowDayCount(window);
  if (n === 1) return `${lastNPrefix} 1 day`;
  return `${lastNPrefix} ${n} days`;
}
